import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '../styles/About.css';
import '../styles/Project.css';
import { translations } from '../translations';
import ImageCarousel from '../components/ImageCarousel';
import { preloadImages } from '../utils/imagePreloader';

const PDF_GALLERY_BBC = 'bbc';
const PDF_GALLERY_ECL = 'ecl';

const About = ({ language }) => {
  const t = translations[language];
  const [selectedCard, setSelectedCard] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfGallery, setPdfGallery] = useState(PDF_GALLERY_BBC);
  const [currentPage, setCurrentPage] = useState(1);
  const [imagesLoading, setImagesLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loadedImages, setLoadedImages] = useState(new Set());
  const scrollPositionRef = useRef(0);
  const loadingTimeoutRef = useRef(null);
  const shouldShowLoadingRef = useRef(false);
  const pdfViewerScrollRef = useRef(0);
  const imageRefs = useRef([]);
  const viewerImageCountRef = useRef(13);

  const baseUrl = process.env.PUBLIC_URL || '';
  const bbcPlanImagePaths = useMemo(() =>
    Array.from({ length: 13 }, (_, i) => {
      const pageNum = String(i + 1).padStart(4, '0');
      return `${baseUrl}/pdf_images/BBC Plan Every 5 Km_page-${pageNum}.jpg`;
    }), [baseUrl]
  );

  const eclNewPdfImagePaths = useMemo(() => {
    const stem = 'ECL_AUTH_56O790_IA_KA_INFRA1_405741_2022_page-';
    return Array.from({ length: 11 }, (_, i) => {
      const pageNum = String(i + 1).padStart(4, '0');
      return `${baseUrl}/new pdf images/${stem}${pageNum}.jpg.jpeg`;
    });
  }, [baseUrl]);

  const viewerImagePaths = useMemo(
    () => (pdfGallery === PDF_GALLERY_ECL ? eclNewPdfImagePaths : bbcPlanImagePaths),
    [pdfGallery, eclNewPdfImagePaths, bbcPlanImagePaths]
  );

  const isKannada = language === 'kannada';
  const readMoreText = isKannada ? 'ಇನ್ನಷ್ಟು ಓದಿ…' : 'Read more…';

  useEffect(() => {
    setMounted(true);
    preloadImages(bbcPlanImagePaths).catch(() => {});
    preloadImages(eclNewPdfImagePaths).catch(() => {});
    return () => {
      setMounted(false);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      shouldShowLoadingRef.current = false;
    };
  }, [bbcPlanImagePaths, eclNewPdfImagePaths]);

  useEffect(() => {
    if (selectedCard !== null) {
      const currentScrollY = window.scrollY;
      scrollPositionRef.current = currentScrollY;
      
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${currentScrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.height = '100%';
      
      const isInsideModal = (target) => {
        if (!target || typeof target.closest !== 'function') return false;
        return target.closest('.fact-modal-content') !== null;
      };
      
      const preventScroll = (e) => {
        if (!isInsideModal(e.target)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      };
      
      const preventWheel = (e) => {
        if (!isInsideModal(e.target)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      };
      
      const preventTouchMove = (e) => {
        if (!isInsideModal(e.target)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      };
      
      window.addEventListener('wheel', preventWheel, { passive: false });
      window.addEventListener('touchmove', preventTouchMove, { passive: false });
      window.addEventListener('scroll', preventScroll, { passive: false });
      document.addEventListener('wheel', preventWheel, { passive: false });
      document.addEventListener('touchmove', preventTouchMove, { passive: false });
      
      return () => {
        window.removeEventListener('wheel', preventWheel);
        window.removeEventListener('touchmove', preventTouchMove);
        window.removeEventListener('scroll', preventScroll);
        document.removeEventListener('wheel', preventWheel);
        document.removeEventListener('touchmove', preventTouchMove);
        
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.height = '';
        document.documentElement.style.overflow = '';
        document.documentElement.style.height = '';
        
        window.scrollTo(0, scrollPositionRef.current);
      };
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    }
  }, [selectedCard]);

  useEffect(() => {
    if (!selectedCard) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setSelectedCard(null);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [selectedCard]);

  const preventContextMenu = useCallback((e) => {
    e.preventDefault();
    return false;
  }, []);

  const preventDragStart = useCallback((e) => {
    e.preventDefault();
    return false;
  }, []);

  const preventKeyboardShortcuts = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S' || e.key === 'p' || e.key === 'P' || e.key === 'u' || e.key === 'U' || e.key === 'i' || e.key === 'I')) {
      e.preventDefault();
      return false;
    }
    if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C'))) {
      e.preventDefault();
      return false;
    }
  }, []);

  const preventSelection = useCallback((e) => {
    e.preventDefault();
    return false;
  }, []);

  useEffect(() => {
    if (showPdfViewer) {
      const savedScroll = pdfViewerScrollRef.current || window.scrollY || document.documentElement.scrollTop;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScroll}px`;
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';
    } else {
      const savedScroll = pdfViewerScrollRef.current;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.documentElement.style.overflow = '';
      setTimeout(() => {
        window.scrollTo({ top: savedScroll, behavior: 'instant' });
      }, 0);
    }
  }, [showPdfViewer]);

  useEffect(() => {
    if (!showPdfViewer) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setShowPdfViewer(false);
    };
    const handleKeyboardShortcutsLocal = (e) => {
      preventKeyboardShortcuts(e);
    };
    window.addEventListener('keydown', handleEscape);
    window.addEventListener('keydown', handleKeyboardShortcutsLocal, true);
    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('selectstart', preventSelection);
    return () => {
      window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('keydown', handleKeyboardShortcutsLocal, true);
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('selectstart', preventSelection);
    };
  }, [showPdfViewer, preventContextMenu, preventSelection, preventKeyboardShortcuts]);

  const handleImageLoad = useCallback((index) => {
    setLoadedImages((prev) => {
      const newSet = new Set(prev);
      newSet.add(index);
      return newSet;
    });
    if (loadingTimeoutRef.current) {
      shouldShowLoadingRef.current = false;
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    /* Do not wait for all pages — lazy pages load on scroll; hiding after page 1 avoids UI freeze */
    if (index === 0) {
      setImagesLoading(false);
    }
  }, []);

  const handleImageError = useCallback((index) => {
    handleImageLoad(index);
  }, [handleImageLoad]);

  const openPdfGallery = useCallback((gallery, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    pdfViewerScrollRef.current = window.scrollY || document.documentElement.scrollTop;
    setPdfGallery(gallery);
    viewerImageCountRef.current = gallery === PDF_GALLERY_ECL ? eclNewPdfImagePaths.length : bbcPlanImagePaths.length;
    imageRefs.current = [];
    setCurrentPage(1);
    setImagesLoading(false);
    setLoadedImages(new Set());
    setShowPdfViewer(true);

    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    shouldShowLoadingRef.current = true;
    loadingTimeoutRef.current = setTimeout(() => {
      if (shouldShowLoadingRef.current) setImagesLoading(true);
    }, 500);
  }, [bbcPlanImagePaths.length, eclNewPdfImagePaths.length]);

  const handleViewKeyPlan = useCallback(
    (e) => openPdfGallery(PDF_GALLERY_BBC, e),
    [openPdfGallery]
  );

  const handleViewEclPdf = useCallback(
    (e) => openPdfGallery(PDF_GALLERY_ECL, e),
    [openPdfGallery]
  );

  const handleCloseModal = useCallback((e) => {
    if (e.target === e.currentTarget || e.target.closest('.fact-modal-close')) {
      setSelectedCard(null);
    }
  }, []);

  const handleClosePdfViewer = useCallback((e) => {
    if (e.target === e.currentTarget || e.target.closest('.pdf-viewer-close')) {
      setShowPdfViewer(false);
    }
  }, []);

  useEffect(() => {
    if (!showPdfViewer || !mounted) return;

    const observers = imageRefs.current.slice(0, viewerImagePaths.length).map((ref, index) => {
      if (!ref) return null;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
              setCurrentPage(index + 1);
            }
          });
        },
        { threshold: 0.5, rootMargin: '-20% 0px -20% 0px' }
      );
      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, [showPdfViewer, mounted, viewerImagePaths.length]);

  const icons = useMemo(() => [
    <svg key="scope" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="19" r="3"></circle>
      <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"></path>
      <circle cx="18" cy="5" r="3"></circle>
    </svg>,
    <svg key="width" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 10v.2A3 3 0 0 1 8.9 16H5a3 3 0 0 1-1-5.8V10a3 3 0 0 1 6 0Z"></path>
      <path d="M7 16v6"></path>
      <path d="M13 19v3"></path>
      <path d="M12 19h8.3a1 1 0 0 0 .7-1.7L18 14h.3a1 1 0 0 0 .7-1.7L16 9h.2a1 1 0 0 0 .8-1.7L13 3l-1.4 1.5"></path>
    </svg>,
    <svg key="connectivity" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="6" x2="6" y1="3" y2="15"></line>
      <circle cx="18" cy="6" r="3"></circle>
      <circle cx="6" cy="18" r="3"></circle>
      <path d="M18 9a9 9 0 0 1-9 9"></path>
    </svg>,
    <svg key="smoothTravel" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="20" height="8" rx="1"></rect>
      <path d="M17 14v7"></path>
      <path d="M7 14v7"></path>
      <path d="M17 3v3"></path>
      <path d="M7 3v3"></path>
      <path d="M10 14 2.3 6.3"></path>
      <path d="m14 6 7.7 7.7"></path>
      <path d="m8 6 8 8"></path>
    </svg>,
    <svg key="investment" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3h12"></path>
      <path d="M6 8h12"></path>
      <path d="m6 13 8.5 8"></path>
      <path d="M6 13h3"></path>
      <path d="M9 13c6.667 0 6.667-10 0-10"></path>
    </svg>,
    <svg key="commercial" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 21v-5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v5"></path>
      <path d="M17.774 10.31a1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.451 0 1.12 1.12 0 0 0-1.548 0 2.5 2.5 0 0 1-3.452 0 1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.77-3.248l2.889-4.184A2 2 0 0 1 7 2h10a2 2 0 0 1 1.653.873l2.895 4.192a2.5 2.5 0 0 1-3.774 3.244"></path>
      <path d="M4 10.95V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8.05"></path>
    </svg>
  ], []);

  const selectedFact = selectedCard !== null && t.about.quickFacts[selectedCard] && typeof t.about.quickFacts[selectedCard] === 'object' ? t.about.quickFacts[selectedCard] : null;

  return (
    <>
      <section id="about" className="about-section">
        <ImageCarousel language={language} />

        <div className="container">
          <div className="about-content">
            <div className="about-content-section">
              <h3 className="content-headline">{t.about.headline}</h3>
              <div className="content-body">
                <p>{t.about.bodyCopy}</p>
              </div>
            </div>

            <div id="project" className="project-content" style={{ marginTop: '3rem', marginBottom: '3rem' }}>
              <div className="section-header">
                <h2 className="section-title">{t.project.title}</h2>
                <h3 className="section-subtitle">{t.project.subtitle}</h3>
              </div>
              <div className="project-intro">
                <div className="card card-highlight">
                  <p className="card-text">{t.project.description1}</p>
                </div>
              </div>
            </div>

            <div className="quick-facts-section">
              <div className="section-header">
                <h3 className="section-title">{t.about.quickFactsTitle}</h3>
              </div>
              <div className="facts-grid">
                {t.about.quickFacts.map((fact, index) => {
                  const factData = typeof fact === 'string' ? { heading: fact, content: '', readMoreContent: '' } : fact;
                  
                  return (
                    <div 
                      key={index} 
                      className="fact-card"
                      onClick={() => factData.readMoreContent && setSelectedCard(index)}
                    >
                      <div className="fact-icon">{icons[index]}</div>
                      <h4 className="fact-heading">{factData.heading}</h4>
                      {factData.content && (
                        <p className="fact-content">
                          {factData.content}
                          {factData.readMoreContent && (
                            <span className="fact-read-more"> {readMoreText}</span>
                          )}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="project-content prr1-button-wrap">
              <div
                className="prr1-button-group"
                role="group"
                aria-label={`${t.project.prr1Button}; ${t.project.prr1ButtonEnvironmentClearance}`}
              >
                <button
                  type="button"
                  className="btn btn-primary prr1-button"
                  onClick={handleViewKeyPlan}
                >
                  {t.project.prr1Button}
                </button>
                <button
                  type="button"
                  className="btn btn-primary prr1-button prr1-button--clearance"
                  onClick={handleViewEclPdf}
                >
                  {t.project.prr1ButtonEnvironmentClearance}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showPdfViewer && mounted && createPortal(
        <div 
          className="pdf-viewer-overlay" 
          onClick={handleClosePdfViewer}
          onContextMenu={preventContextMenu}
          onDragStart={preventDragStart}
          onSelectStart={preventSelection}
          onKeyDown={preventKeyboardShortcuts}
        >
          <div 
            className="pdf-viewer-container" 
            onClick={(e) => e.stopPropagation()}
            onContextMenu={preventContextMenu}
            onDragStart={preventDragStart}
            onSelectStart={preventSelection}
          >
            <div className="pdf-viewer-header">
              <button 
                className={`pdf-viewer-close ${isKannada ? 'pdf-viewer-close-kn' : 'pdf-viewer-close-en'}`}
                onClick={() => setShowPdfViewer(false)} 
                aria-label="Back"
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5"></path>
                  <path d="M12 19l-7-7 7-7"></path>
                </svg>
                <span>{isKannada ? 'ಹಿಂದೆ' : 'Back'}</span>
              </button>
              <div className="pdf-viewer-page-indicator">
                <span>{isKannada ? `ಪುಟ ${currentPage} / ${viewerImagePaths.length}` : `Page ${currentPage} / ${viewerImagePaths.length}`}</span>
              </div>
            </div>
            <div className="pdf-viewer-content">
              {imagesLoading && (
                <div className="pdf-viewer-loading">
                  <div className="loading-spinner"></div>
                  <p className="loading-text">{isKannada ? 'ಬ್ಲೂ ಪ್ರಿಂಟ್ ಲೋಡ್ ಮಾಡಲಾಗುತ್ತಿದೆ...' : 'Loading blue print...'}</p>
                </div>
              )}
              <div 
                className={`pdf-images-container ${imagesLoading ? 'pdf-images-loading' : ''}`}
                onContextMenu={preventContextMenu}
                onDragStart={preventDragStart}
                onSelectStart={preventSelection}
              >
                <div className="pdf-image-protection-overlay"></div>
                {viewerImagePaths.map((imagePath, index) => (
                  <div key={`${pdfGallery}-${index}`} className="pdf-image-wrapper">
                    <img
                      ref={(el) => {
                        imageRefs.current[index] = el;
                      }}
                      src={imagePath}
                      alt={
                        pdfGallery === PDF_GALLERY_ECL
                          ? `PRR-1 document page ${index + 1}`
                          : `BBC Blue Print Page ${index + 1}`
                      }
                      className="pdf-image"
                      loading={index === 0 ? 'eager' : 'lazy'}
                      fetchPriority={index === 0 ? 'high' : 'low'}
                      decoding="async"
                      draggable="false"
                      onContextMenu={preventContextMenu}
                      onDragStart={preventDragStart}
                      onSelectStart={preventSelection}
                      onLoad={() => handleImageLoad(index)}
                      onError={() => handleImageError(index)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {selectedFact && mounted && createPortal(
        <div className="fact-modal-overlay" onClick={handleCloseModal}>
          <div className="fact-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="fact-modal-close" 
              onClick={() => setSelectedCard(null)} 
              aria-label="Close"
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18"></path>
                <path d="M6 6l12 12"></path>
              </svg>
            </button>
            <div className="fact-modal-body">
              <div className="fact-modal-icon">{icons[selectedCard]}</div>
              <h2 className="fact-modal-heading">{selectedFact.heading}</h2>
              <p className="fact-modal-text">
                {selectedFact.content} {selectedFact.readMoreContent}
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default About;
